import {
    BLOCK_CODE,
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_DIFF,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    FRAGMENT_EXPANDABLE_TITLE,
    LIST_BLOCKS,
} from '@gitbook/doc-core';
import { Block, type Document, type Fragment, type Inline, type Text } from '@gitbook/slate';

// Spacing between blocks
export const BLOCKS_SPACING_DEFAULT = 16;

function unwrapDiffIfNecessary(node: Block | Inline | Text | undefined) {
    if (!node || !Block.isBlock(node)) {
        return node;
    }
    return unwrapDiff(node);
}

function cacheFn<T>(fn: () => T) {
    let value: T | undefined;
    return () => {
        if (value !== undefined) {
            return value;
        }
        value = fn();
        return value;
    };
}

/**
 * Get the spacing for a node.
 */
export function getBlockSpacing(
    node: Block,
    parent: Block | Document | Fragment
): { paddingTop: number; paddingBottom: number } {
    const isDiffNode = node.type === BLOCK_DIFF;
    // biome-ignore lint/style/noNonNullAssertion: BLOCK_DIFF always has at least one child
    const targetNode = isDiffNode ? node.blockNodes.first()! : node;
    const parentType = parent.type;
    const nodeType = targetNode.type;

    const getFragmentType = cacheFn(() => {
        const trueNodeKey = getTrueNodeKey();
        return parent.getFragmentParent(trueNodeKey)?.type ?? null;
    });

    const getTrueNodeKey = cacheFn(() => {
        const trueNode = getTrueNode(targetNode, parent);
        return trueNode.key;
    });

    const getIndex = cacheFn(() => {
        const trueNodeKey = getTrueNodeKey();
        return parent.getChildIndex(trueNodeKey);
    });

    const getPrevSibling = cacheFn(() => {
        const index = getIndex();
        return unwrapDiffIfNecessary(parent.nodes.get(index - 1)) ?? null;
    });

    const getNextSibling = cacheFn(() => {
        const index = getIndex();
        return unwrapDiffIfNecessary(parent.nodes.get(index + 1)) ?? null;
    });

    const checkIsFirst = () => getIndex() === 0;
    const checkIsLast = () => getIndex() === parent.nodes.size - 1;

    const siblingOverride = (() => {
        switch (nodeType) {
            case BLOCK_EXPANDABLE: {
                return {
                    paddingTop: getPrevSibling()?.type === BLOCK_EXPANDABLE ? 0 : 16,
                    paddingBottom: getNextSibling()?.type === BLOCK_EXPANDABLE ? 0 : 16,
                };
            }
        }
        return {};
    })();

    const fragmentOverride = (() => {
        switch (getFragmentType()) {
            case FRAGMENT_EXPANDABLE_TITLE: {
                return {
                    paddingTop: 16,
                    paddingBottom: 16,
                };
            }
            default:
                return {};
        }
    })();

    const parentOverride = (() => {
        switch (parentType) {
            case BLOCK_HINT: {
                return {
                    paddingTop: checkIsFirst() ? 16 : 4,
                    paddingBottom: checkIsLast() ? 16 : 4,
                };
            }
            case BLOCK_STEPPER_STEP: {
                if (checkIsFirst()) {
                    return { paddingTop: 0 };
                }
                if (checkIsLast()) {
                    return { paddingBottom: 0 };
                }
                return {};
            }
            case BLOCK_COLUMN: {
                // We want to make sure that all first/last blocks in a column are aligned between the columns
                if (checkIsFirst()) {
                    return { paddingTop: 0 };
                }
                if (checkIsLast()) {
                    return { paddingBottom: nodeType === BLOCK_TABLE ? 12 : 0 };
                }
                return {};
            }
            case BLOCK_QUOTE:
                return { paddingTop: 4, paddingBottom: 4 };
            case BLOCK_LIST_ITEM: {
                // We reset styling from all list item children except the child list blocks
                // BLOCK_LIST_ITEM provides the spacing for the list item by its own
                if (!LIST_BLOCKS.includes(nodeType)) {
                    return { paddingTop: 0, paddingBottom: 0 };
                }

                const result = { paddingTop: 8, paddingBottom: 8 };
                if (checkIsFirst()) {
                    result.paddingTop = 0;
                }
                if (checkIsLast()) {
                    result.paddingBottom = 0;
                }
                return result;
            }
            case BLOCK_TABLE: {
                if ([BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3].includes(nodeType)) {
                    return { paddingTop: 0, paddingBottom: 0 };
                }
            }
        }

        return {};
    })();

    const nodeSpacing = (() => {
        switch (nodeType) {
            case BLOCK_HEADING_1:
            case BLOCK_HEADING_2:
            case BLOCK_HEADING_3:
                return { paddingTop: 24, paddingBottom: 8 };
            case BLOCK_PARAGRAPH:
                return { paddingTop: 8, paddingBottom: 8 };
            case BLOCK_LIST_OL:
            case BLOCK_LIST_UL:
            case BLOCK_LIST_TASKS:
                return {
                    paddingTop: 0,
                    paddingBottom: 8,
                };
            case BLOCK_HINT:
                return { paddingTop: 16, paddingBottom: 16 };
            case BLOCK_CODE:
                return { paddingTop: 16, paddingBottom: 16 };
            case BLOCK_LIST_ITEM:
                return {
                    paddingTop: checkIsFirst() ? 0 : 4,
                    paddingBottom: checkIsLast() ? 0 : 4,
                };
            case BLOCK_REUSABLE_CONTENT:
            case BLOCK_IF:
            case BLOCK_COLUMNS:
                return { paddingTop: 1, paddingBottom: 1 };
            case BLOCK_STEPPER_STEP:
            case BLOCK_COLUMN:
            case BLOCK_EXPANDABLE:
            case BLOCK_DIFF:
                return { paddingTop: 0, paddingBottom: 0 };
            case BLOCK_TABLE:
                return { paddingTop: 16, paddingBottom: 24 };
            default:
                return {
                    paddingTop: BLOCKS_SPACING_DEFAULT,
                    paddingBottom: BLOCKS_SPACING_DEFAULT,
                };
        }
    })();

    return {
        ...nodeSpacing,
        ...siblingOverride,
        ...parentOverride,
        ...fragmentOverride,
    };
}

/**
 * When it's a diff mode the node we render is a first child of the BLOCK_DIFF.
 * In some cases we need to find the index of the rendered element.
 * If we pass the child node of the BLOCK_DIFF we can't find the index of the node,
 * because it's not a direct child of the parent.
 * In this case we try to find BLOCK_DIFF that is a direct parent of the given node and use it
 * to find the index of the rendered element.
 */
function getTrueNode(
    node: Block,
    parent: Block | Document | Fragment
): Block | Document | Fragment {
    return (
        (parent.nodes.find(
            (child) =>
                child.type === BLOCK_DIFF &&
                // It's possible that the parent has multiple BLOCK_DIFF children
                // So we need to find the exact one, that is a direct parent of the given node
                child.nodes.some((_child) => _child.key === node.key)
        ) as Block) || node
    );
}

function unwrapDiff(node: Block): Block {
    let result = node;
    while (result.type === BLOCK_DIFF) {
        // biome-ignore lint/style/noNonNullAssertion: BLOCK_DIFF always has at least one child
        result = result.blockNodes.first()!; // Get the first child of the BLOCK_DIFF
    }
    return result;
}
